import { DEFAULT_PAGE_SIZE } from '@/constants';
import { ListResponse } from '@/types/common';
import {
  ActivationMode,
  MemberInfo,
  // MemberStatistic,
  // MemberStatus,
  UserMemberCardActionType,
  UserMemberCardInfo,
} from '@/types/member';
import {
  UnassignedClueList,
  SourceInfo,
  FollowUpInfo,
  QuickFilterResponseItem,
} from '@/types/clue';
import { requestWrapper } from '@/utils/request';
import { IStudioInfo } from '@/types/studio';
import { StudioStatus } from '@/types/studio';
import { ClueAutoAssignSwitch } from '@/types/clue';

export type CreateUserMemberCardParams = {
  id: number;
  user_id: number; // 用户 ID，关联用户表的主键，一个用户可有多条开卡记录
  activate_type: ActivationMode; // 激活方式：1:立即激活 2:约课, 或者到期自动激活
  member_card_id: number; // 会员卡 ID，关联会员卡表的 card_id
  pay_image: string; // 付款凭证图片的名称
  auto_activate_days: number; // 自动激活的天数
  actual_amount: number; // 实收金额，单位为分
  issuing_store_id: number; // 开卡门店的 ID
  issuing_admin_id: number; // 开卡的负责人 ID
  issuing_remarks: string; // 开卡的备注信息
  stop_days: number; // 停卡时长
  return_amount: number; // 退卡金额
  exp_time: string; // 新有效期至
  action_remark: string; // 操作备注
  action_type: UserMemberCardActionType; // 操作类型
  reason: string; // 停卡原因
  source_type: string; // 来源类型
};

// 请求会员列表的参数
export type GetMemberListParams = {
  page: number; // 当前页码，默认为 1
  page_size: number; // 每页显示的记录数
  merchant_id: number; // 商户 ID
  store_id: number; // 门店 ID，必填
  admin_id: number; // 负责人 ID
  name: string; // 姓名
  phone_number: string; // 手机号
  wx_number: string; // 微信
  source_id: number; // 线索来源
  gender: number; // 性别
  remark: string; // 备注
  create_stime: number; // 创建时间
  create_etime: number; // 创建时间
  assign_stime: string; // 分配时间
  assign_etime: string; // 分配时间
  trial_course_stime: string; // 试课时间
  trial_course_etime: string; // 试课时间
  last_follow_up_stime: string; // 最后跟进时间
  last_follow_up_etime: string; // 最后跟进时间
  status: number | number[]; // 状态
  lead_id: number; // 线索 ID
  user_id: number; // 用户 ID
  notes: string; // 跟进信息
  image_url: string; // 图片附件
  coupon_id: number; // 体验券 ID
  price: number; // 价格
  createDateRange?: [string, string];
  keywords?: string; // 搜索关键词
  id?: number; // 线索 ID
  delete_id?: number; // 删除的线索 ID
  reason?: string; // 合并原因

  assignDateRange?: [string, string];
  tryDateRange?: [string, string];
  lastDateRange?: [string, string];
};

// 获取会员详情
export async function getMemberInfo(uid: number) {
  return requestWrapper<MemberInfo>('/api/user_member/info', {
    method: 'GET',
    params: { user_id: uid },
  });
}

// 更新会员基本信息
export async function updateMemberInfo(body: Partial<MemberInfo>) {
  return requestWrapper<MemberInfo>('/api/user_member/update', {
    method: 'POST',
    data: body,
  });
}

// 会员开卡
export async function createUserMemberCard(body: Partial<CreateUserMemberCardParams>) {
  return requestWrapper<{ id: number }>('/api/user_member_card/create', {
    method: 'POST',
    data: body,
  });
}

// 更新会员卡
export async function updateUserMemberCard(body: Partial<CreateUserMemberCardParams>) {
  return requestWrapper<{ id: number }>('/api/user_member_card/update', {
    method: 'POST',
    data: body,
  });
}

// 获取用户拥有的会员卡列表
export async function getUserMemberCardList(params: { user_id: number }) {
  return requestWrapper<UserMemberCardInfo[]>('/api/user_member_card/list', {
    method: 'GET',
    params,
  });
}

// 获取会员数量统计
// export async function getMemberStatistic(params: {
//   store_id: number; // 门店 ID
//   is_member: MemberStatus; // 是否是会员，0 - 否，1有效会员，2无效会员
// }) {
//   return requestWrapper<MemberStatistic>('/api/user_member_card/statistic', {
//     method: 'GET',
//     params,
//   });
// }

// 获取未分配线索列表
export async function getPendingList(params: Partial<GetMemberListParams> = {}) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    // is_member: MemberStatus.VALID,
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  return requestWrapper<ListResponse<UnassignedClueList>>('/api/leads/unassigned_list', {
    method: 'GET',
    params: mergedParams,
  });
}
// 获取线索来源列表
export async function getSourceList(params: Partial<GetMemberListParams> = {}) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    // is_member: MemberStatus.VALID,
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  return requestWrapper<ListResponse<SourceInfo>>('/api/leads/source_list', {
    method: 'GET',
    params: mergedParams,
  });
}
export type AddClueResponse = ListResponse<MemberInfo> & {
  lead_id?: number;
};

// 添加线索
export async function addClue(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<AddClueResponse>('/api/leads/create', {
    method: 'POST',
    data: params,
  });
}
// 更新线索
export async function updateClue(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/update', {
    method: 'POST',
    data: params,
  });
}

//门店线索列表
export async function getStoreClueList(params: {
  store_id: number;
  assign_stime?: number;
  assign_etime?: number;
  create_stime?: number;
  create_etime?: number;
  last_follow_up_stime?: number;
  last_follow_up_etime?: number;
  trial_course_stime?: number;
  trial_course_etime?: number;
  status?: number;
  source_id?: number;
  admin_id?: number;
  page?: number;
  page_size?: number;
}) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  return requestWrapper<ListResponse<UnassignedClueList>>('/api/leads/list', {
    method: 'GET',
    params: mergedParams,
  });
}

// 获取门店列表
export async function getStudioList(
  params: {
    page?: number;
    page_size?: number;
    merchant_id?: string; // 商户 ID 列表
    status?: StudioStatus; // 状态 0: 全部， 1: 在线， 2: 下线
  } = {},
) {
  const defaultParams = {
    page: 1,
    page_size: 100,
    // merchant_id: 1,
    status: StudioStatus.ONLINE,
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };

  return requestWrapper<ListResponse<IStudioInfo>>('/api/store/list', {
    method: 'GET',
    params: mergedParams,
  });
}
// 线索详情
export async function getClueDetail(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<UnassignedClueList>('/api/leads/info', {
    method: 'GET',
    params,
  });
}
//跟进记录
export async function getFollowUpList(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<FollowUpInfo>>('/api/leads/follow_up_list', {
    method: 'GET',
    params,
  });
}
//试课记录
export async function getTrialRecordList(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/trial_course_list', {
    method: 'GET',
    params,
  });
}
//新建跟进
export async function addFollowUp(params: Partial<GetMemberListParams> = {}) {
  // console.log('params===', params);

  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/create_follow_up', {
    method: 'POST',
    data: params,
  });
}
//分配线索
export async function assignClue(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/assign', {
    method: 'POST',
    data: params,
  });
}

export interface QuickFilterStats {
  today_unassigned: number;
  yesterday_unassigned: number;
  seven_days_unassigned: number;
}

export async function getQuickFilterStats() {
  return requestWrapper<QuickFilterStats>('/api/v1/clue/quick-filter/stats', {
    method: 'GET',
  });
}

// interface ApiResponse<T> {
//   code: number;
//   message: string;
//   data: T;
// }

// 根据手机号搜索用户
// export async function searchUsersByPhone(params: { phone_number: string }) {
//   return requestWrapper<SearchUserInfo>('/api/user/get_by_phone_number', {
//     method: 'GET',
//     params,
//   });
// }

//修改关联用户
export async function updateRelatedUser(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/update_user', {
    method: 'POST',
    data: params,
  });
}

//搜索线索
// export async function searchClue(params: Partial<GetMemberListParams> = {}) {
//   return requestWrapper<ListResponse<MemberInfo>>('/api/leads/search', {
//     method: 'GET',
//     params,
//   });
// }

//合并线索
export async function mergeClue(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/leads/merge', {
    method: 'POST',
    data: params,
  });
}

//用户详情跟进记录
export async function getUserFollowUpList(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<FollowUpInfo>>('/api/user/follow_up_list', {
    method: 'GET',
    params,
  });
}

//创建用户详情跟进记录
export async function createUserFollowUp(params: Partial<GetMemberListParams> = {}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/user/create_follow_up', {
    method: 'POST',
    data: params,
  });
}

//线索快捷筛选条数
export async function getQuickFilterCount(params: { store_id: number }) {
  return requestWrapper<ListResponse<QuickFilterResponseItem>>('/api/leads/list_info', {
    method: 'GET',
    params,
  });
}

//未分配线索快捷筛选条数
export async function getUnassignedClueQuickFilterCount() {
  return requestWrapper<ListResponse<QuickFilterResponseItem>>('/api/leads/unassigned_list_info', {
    method: 'GET',
    // params,
  });
}

// 更新门店员工自动分配线索配置
export async function updateStoreAdminSettings(params: {
  store_id: number;
  items: { admin_id: number; is_leads_auto_assign: ClueAutoAssignSwitch }[];
}) {
  return requestWrapper<ListResponse<MemberInfo>>('/api/admin/store/auto_assign/batch', {
    method: 'POST',
    data: params,
  });
}
